import { useState, useEffect } from "react";
import { api, fileUrl } from "../../lib/api";
import { newImageElement, newVideoElement } from "../../lib/constants";
import { toast } from "sonner";
import { GoogleDriveLogo, Spinner, MagnifyingGlass, Image as ImageIcon } from "@phosphor-icons/react";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";

export default function DriveImport({ onAdd, onImported }) {
  const [connected, setConnected] = useState(false);
  const [open, setOpen] = useState(false);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    api.get("/drive/status").then((r) => setConnected(r.data.connected)).catch(() => {});
  }, []);

  const loadFiles = async (q = "") => {
    setLoading(true);
    try {
      const res = await api.get("/drive/files", { params: q ? { q } : {} });
      setFiles(res.data.files || []);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not load Drive files";
      toast.error("Google Drive", { description: msg, duration: 9000 });
    } finally {
      setLoading(false);
    }
  };

  const startConnect = async () => {
    try {
      const res = await api.get("/drive/connect");
      const popup = window.open(res.data.authorization_url, "drive-auth", "width=520,height=660");
      if (!popup || popup.closed || typeof popup.closed === "undefined") {
        toast.error("Your browser blocked the Google window", {
          description: "Open FlyerStudio in its own browser tab, then try Connect Google Drive again.",
          duration: 8000,
        });
        return;
      }
      const onMsg = (e) => {
        if (e.data === "easydesign-drive-connected") {
          cleanup();
          setConnected(true);
          setOpen(true);
          loadFiles();
          toast.success("Google Drive connected!");
        } else if (e.data === "easydesign-drive-error") {
          cleanup();
          toast.error("Drive connection failed");
        }
      };
      const poll = setInterval(async () => {
        if (popup && popup.closed) {
          cleanup();
          const s = await api.get("/drive/status").catch(() => ({ data: { connected: false } }));
          if (s.data.connected) { setConnected(true); setOpen(true); loadFiles(); }
        }
      }, 800);
      const cleanup = () => { window.removeEventListener("message", onMsg); clearInterval(poll); };
      window.addEventListener("message", onMsg);
    } catch (e) {
      toast.error("Could not start Google sign-in");
    }
  };

  const openDrive = async () => {
    try {
      const res = await api.get("/drive/status");
      if (res.data.connected) { setConnected(true); setOpen(true); loadFiles(); }
      else startConnect();
    } catch (e) {
      startConnect();
    }
  };

  const importFile = async (f) => {
    setImporting(f.id);
    try {
      const res = await api.post("/drive/import", { file_id: f.id });
      const url = fileUrl(res.data.storage_path);
      const isVid = (res.data.content_type || "").startsWith("video");
      onAdd(isVid ? newVideoElement(url) : newImageElement(url));
      onImported && onImported();
      toast.success("Imported from Drive");
      setOpen(false);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Import failed";
      toast.error("Import failed", { description: msg, duration: 8000 });
    } finally {
      setImporting(null);
    }
  };

  return (
    <>
      <Button
        onClick={openDrive}
        variant="outline"
        data-testid="drive-btn"
        className="w-full mb-4 bg-[#0E0E0E] border-white/15 text-white hover:bg-[#1a1a1a]"
      >
        <GoogleDriveLogo weight="fill" size={18} className="mr-2 text-green-400" />
        {connected ? "Import from Google Drive" : "Connect Google Drive"}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#141414] border-white/10 text-white sm:max-w-2xl" data-testid="drive-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              <GoogleDriveLogo weight="fill" size={22} className="text-green-400" /> Your Google Drive files
            </DialogTitle>
          </DialogHeader>
          <div className="relative mb-3">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") loadFiles(search); }}
              placeholder="Search by name, then press Enter…"
              data-testid="drive-search"
              className="w-full h-10 rounded-lg bg-black/40 border border-white/10 pl-9 pr-3 text-sm text-white outline-none focus:border-blue-500/60"
            />
          </div>
          <div className="h-[420px] overflow-y-auto custom-scrollbar">
            {loading ? (
              <div className="h-full flex items-center justify-center text-zinc-400">
                <Spinner className="animate-spin mr-2" size={20} /> Loading…
              </div>
            ) : files.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-zinc-500">
                <ImageIcon size={36} className="mb-2 opacity-50" />
                No images found in your Drive.
              </div>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                {files.map((f) => (
                  <button
                    key={f.id}
                    onClick={() => importFile(f)}
                    disabled={importing === f.id}
                    data-testid={`drive-file-${f.id}`}
                    className="group relative rounded-lg overflow-hidden border border-white/10 hover:border-blue-500/60 transition-all aspect-square bg-[#0E0E0E] flex items-center justify-center"
                  >
                    {f.thumbnailLink ? (
                      <img src={f.thumbnailLink} referrerPolicy="no-referrer" alt={f.name} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />
                    ) : (
                      <ImageIcon size={28} className="text-zinc-500" />
                    )}
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center p-2">
                      {importing === f.id ? <Spinner className="animate-spin" size={20} /> : <span className="text-[11px] text-center font-medium line-clamp-3">{f.name}</span>}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
